import { dist2, idiv, imax, isqrt, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, SendaNpc, WinterRules } from '../types.ts';
import { castSkill } from '../battle/skills.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { cellAtWorld, cellId, heroWalkable } from '../grid.ts';
import { DIRS, hexToWorld, worldToHex, type Hex } from '../hex.ts';
import { gapOpen, winterOrder } from '../battle/druid-winter.ts';
import { sapFlow, sapWays } from '../battle/druid-winter-layout.ts';
import { useSkills } from './ghost.ts';

/**
 * The druid's chapter 4 played by the lab's bot (docs/SENDAS.md, «Calibración»; ai/senda-bot.ts sends it here):
 *
 * - the boughs (`boughs`): who knows goes up the way and, with a block coming down on it or where it is going, off the
 *   creaking spot along the bough (ahead if it can, else back); the crows when they're on it. Who doesn't goes up and
 *   fights what comes, under the creaking or not.
 * - the hollow (`hollow`): who knows treads the bark swelling by Hob or Ceda (if it gets there in time), gets off a
 *   swelling by itself, and between strikes what is out of the wood. Who doesn't stands by Hob and strikes what comes.
 * - the sap (`sap`, the test): who knows works out the knots to close (the crown's way nearest what is closed now),
 *   turns them, strikes the frost off a knot it needs, gets off the claw and the swelling by itself and treads the one
 *   by Hob when it is near. Who doesn't closes the knot of the bud that asks most, then the next, as the obvious goes.
 */

type Pt = { x: number; y: number };
type Sap = NonNullable<WinterRules['sap']>;

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

const guard = (s: BattleState, h: Hero, x: number, y: number, r: number): boolean => {
  const t = nearestTo(
    enemiesNear(s, x, y, r, (e) => targetable(s, e)),
    x,
    y,
  );
  if (!t) return false;
  strike(s, h, t);
  return true;
};

const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** Ticks for the hero to walk from where it is to a point. */
const walkTicks = (h: Hero, p: Pt): number => idiv(isqrt(dist2(h.x, h.y, p.x, p.y)) * 12, imax(1, h.stats.speed));

/** Off a spot to `d` from it: of six ways round the hero (and twice as far), the floor farthest from it and clear of `bad`. */
const away = (s: BattleState, h: Hero, x: number, y: number, d: number, bad: (px: number, py: number) => boolean = () => false): void => {
  const DX = [1000, 500, -500, -1000, -500, 500];
  const DY = [0, -866, -866, 0, 866, 866];
  let bx = h.x;
  let by = h.y;
  let bd = -1;
  for (let k = 0; k < 6; k++) {
    for (const m of [d, idiv(d * 3, 2), idiv(d * 2, 3)]) {
      const tx = h.x + idiv((DX[k] as number) * m, 1000);
      const ty = h.y + idiv((DY[k] as number) * m, 1000);
      if (!heroWalkable(s, cellAtWorld(s, tx, ty)) || bad(tx, ty)) continue;
      const dd = dist2(tx, ty, x, y);
      if (dd > bd) {
        bd = dd;
        bx = tx;
        by = ty;
      }
      break;
    }
  }
  h.target = 0;
  go(s, h, bx, by, 150);
};

/**
 * The nearest floor the hero can walk to (a search over the cells round it, `steps` deep) where `ok` holds; of those
 * as near, the one `score` puts highest.
 */
const refuge = (s: BattleState, h: Hero, steps: number, ok: (x: number, y: number) => boolean, score: (x: number, y: number) => number): Pt | null => {
  const R = s.map.radius;
  const start = worldToHex(h.x, h.y);
  const seen = new Set<string>([`${start.q},${start.r}`]);
  let ring = [start];
  for (let d = 1; d <= steps; d++) {
    const next: Hex[] = [];
    for (const c of ring)
      for (const k of DIRS) {
        const n = { q: c.q + k.q, r: c.r + k.r };
        const key = `${n.q},${n.r}`;
        if (seen.has(key)) continue;
        seen.add(key);
        if (!heroWalkable(s, cellId(R, n.q, n.r))) continue;
        next.push(n);
      }
    let best: Pt | null = null;
    let bs = -0x7fffffff;
    for (const n of next) {
      const p = hexToWorld(n.q, n.r);
      if (!ok(p.x, p.y)) continue;
      const sc = score(p.x, p.y);
      if (sc > bs) {
        bs = sc;
        best = p;
      }
    }
    if (best) return best;
    ring = next;
  }
  return null;
};

/** A serpent's swelling by the hero itself (it stands still): off it. True if it went. */
const offSwelling = (s: BattleState, h: Hero, r: WinterRules): boolean => {
  const w = s.senda?.winter;
  const S = r.serp;
  if (!w || !S) return false;
  for (const id of Object.keys(w.sp)) {
    const m = w.sp[Number(id)] as number[];
    if (m[0] !== 1 || m[4] !== 0) continue;
    if (withinRange(h.x, h.y, m[2] as number, m[3] as number, S.r + 350)) {
      const x = m[2] as number;
      const y = m[3] as number;
      const p = refuge(s, h, 4, (px, py) => !withinRange(px, py, x, y, S.r + 400), (px, py) => -dist2(px, py, h.x, h.y));
      if (p) {
        h.target = 0;
        go(s, h, p.x, p.y, 150);
      } else away(s, h, x, y, S.r + 1000);
      return true;
    }
  }
  return false;
};

/** A swelling by a person that the hero can get onto before it bursts: tread it. True if it went. */
const tread = (s: BattleState, h: Hero, r: WinterRules, far: number): boolean => {
  const w = s.senda?.winter;
  const S = r.serp;
  if (!w || !S) return false;
  let best: number[] | null = null;
  let bt = 0x7fffffff;
  for (const id of Object.keys(w.sp)) {
    const m = w.sp[Number(id)] as number[];
    if (m[0] !== 1 || m[4] === 0) continue;
    const p = { x: m[2] as number, y: m[3] as number };
    const left = (m[1] as number) - s.tick;
    const need = walkTicks(h, p);
    if (need > left + 2 || !withinRange(h.x, h.y, p.x, p.y, far)) continue;
    if (left < bt) {
      bt = left;
      best = m;
    }
  }
  if (!best) return false;
  h.target = 0;
  const x = best[2] as number;
  const y = best[3] as number;
  if (withinRange(h.x, h.y, x, y, S.stomp - 250)) {
    if (h.moving) orderMove(s, h, h.x, h.y);
    return true;
  }
  orderMove(s, h, x, y);
  return true;
};

/* ---------- the boughs ---------- */

/** The nearest point on the way to the hero, as a distance up it (sim units from its start). */
const along = (way: Pt[], x: number, y: number): number => {
  let best = 0;
  let bd = 0x7fffffff;
  let acc = 0;
  for (let i = 1; i < way.length; i++) {
    const a = way[i - 1] as Pt;
    const b = way[i] as Pt;
    const L = imax(1, isqrt(dist2(a.x, a.y, b.x, b.y)));
    for (let k = 0; k <= 8; k++) {
      const px = a.x + idiv((b.x - a.x) * k, 8);
      const py = a.y + idiv((b.y - a.y) * k, 8);
      const d = dist2(px, py, x, y);
      if (d < bd) {
        bd = d;
        best = acc + idiv(L * k, 8);
      }
    }
    acc += L;
  }
  return best;
};

/** The point at a distance up the way. */
const at = (way: Pt[], d: number): Pt => {
  let acc = 0;
  for (let i = 1; i < way.length; i++) {
    const a = way[i - 1] as Pt;
    const b = way[i] as Pt;
    const L = imax(1, isqrt(dist2(a.x, a.y, b.x, b.y)));
    if (acc + L >= d) {
      const k = imax(0, d - acc);
      return { x: a.x + idiv((b.x - a.x) * k, L), y: a.y + idiv((b.y - a.y) * k, L) };
    }
    acc += L;
  }
  return way[way.length - 1] as Pt;
};

const boughsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.winter?.boughs;
  const w = s.senda?.winter;
  if (!r || !w) return;
  const here = along(r.way, h.x, h.y);
  if (!knows) {
    if (guard(s, h, h.x, h.y, 2600)) return;
    h.target = 0;
    return go(s, h, at(r.way, here + 2000).x, at(r.way, here + 2000).y);
  }
  const R = r.snow.r + 250;
  const under = (x: number, y: number): boolean => w.blocks.some((b) => withinRange(x, y, b[0] as number, b[1] as number, R));
  // A block coming down on it: out from under, to the nearest floor it can reach clear of every block (up first).
  if (under(h.x, h.y)) {
    const p = refuge(s, h, 5, (x, y) => !under(x, y), (x, y) => along(r.way, x, y));
    if (p) {
      h.target = 0;
      return go(s, h, p.x, p.y, 150);
    }
  }
  // Crows on it: strike them (standing still is safe until the next creak).
  if (guard(s, h, h.x, h.y, 2200)) return;
  // A gap ahead with the boughs apart: wait at its end (dodging, striking) until the wind swings them together.
  for (let i = 0; i < r.gaps.length; i++) {
    const g = r.gaps[i] as { a: Pt; b: Pt };
    const ga = along(r.way, g.a.x, g.a.y);
    if (ga < here - 400 || ga > here + 2600) continue;
    if (gapOpen(r, i, s.waveT) && gapOpen(r, i, s.waveT + walkTicks(h, g.b) + 4)) {
      h.target = 0;
      return go(s, h, g.b.x, g.b.y, 200);
    }
    if (withinRange(h.x, h.y, g.a.x, g.a.y, 500)) return void guard(s, h, h.x, h.y, 6000);
    h.target = 0;
    return go(s, h, g.a.x, g.a.y, 200);
  }
  // Up the way, short of a spot that is creaking.
  const next = at(r.way, here + 1600);
  if (under(next.x, next.y)) {
    if (h.moving) orderMove(s, h, h.x, h.y);
    return void guard(s, h, h.x, h.y, 6000);
  }
  h.target = 0;
  go(s, h, next.x, next.y, 400);
};

/* ---------- the hollow ---------- */

const hollowBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.winter;
  const w = s.senda?.winter;
  const hob = npcOf(s, 'hob');
  if (!r || !w || !hob) return;
  if (!knows) {
    if (guard(s, h, hob.x, hob.y, 6000)) return;
    h.target = 0;
    return go(s, h, hob.x + 900, hob.y + 500);
  }
  if (offSwelling(s, h, r)) return;
  if (tread(s, h, r, 9000)) return;
  // Out of the wood: strike it.
  if (guard(s, h, h.x, h.y, 5000)) return;
  // Between: near them, but moving a little (still is where they come out).
  const t = (idiv(s.tick, 18) % 6) as number;
  const DX = [1600, 800, -800, -1600, -800, 800];
  const DY = [0, -1400, -1400, 0, 1400, 1400];
  h.target = 0;
  go(s, h, hob.x + 500 + (DX[t] as number), hob.y + (DY[t] as number), 200);
};

/* ---------- the sap of the crown ---------- */

/** The knots to turn now: who knows, the crown's way nearest what is closed; who doesn't, the obvious (the biggest asks first). */
const plan = (r: Sap, s: BattleState, knows: boolean): number[] => {
  const w = s.senda?.winter;
  if (!w) return [];
  const root = r.roots[w.stage] as number;
  const mine = r.knots.map((_, i) => i).filter((i) => (r.nodes[(r.knots[i] as { node: number }).node] as { stage: number }).stage === w.stage);
  if (knows) {
    const ways = sapWays(r.nodes, r.knots, root, r.need);
    let best: number[] = [];
    let bd = 99;
    for (const way of ways) {
      const want = new Set(way);
      const diff = mine.filter((i) => (w.closed[i] === 1) !== want.has(i));
      if (diff.length < bd) {
        bd = diff.length;
        best = diff;
      }
    }
    return best;
  }
  // The obvious: shut the bud that asks most, then the next; never open one again.
  const order = [...mine].sort((a, b) => ((r.nodes[(r.knots[b] as { node: number }).node] as { need: number }).need - (r.nodes[(r.knots[a] as { node: number }).node] as { need: number }).need) || a - b);
  const flow = sapFlow(r.nodes, r.knots, root, w.closed.map((x) => x === 1));
  const open = flow.filter((x) => x === 0).length;
  if (open >= r.need) return [];
  const next = order.find((i) => !w.closed[i]);
  return next === undefined ? [] : [next];
};

const sapBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const R = s.rules?.senda?.winter;
  const r = R?.sap;
  const w = s.senda?.winter;
  if (!R || !r || !w || w.done >= 0 || !w.closed.length) return;
  // Off the claw (unless the knot is all but turned).
  if (w.claw >= 0 && withinRange(h.x, h.y, w.cx, w.cy, r.boss.claw.r + 350) && !(w.job >= 0 && w.held >= r.cut - 4 && w.claw - s.tick > 6)) {
    if (knows) {
      w.job = -1;
      const p = refuge(s, h, 5, (px, py) => !withinRange(px, py, w.cx, w.cy, r.boss.claw.r + 400), (px, py) => -dist2(px, py, h.x, h.y));
      if (p) {
        h.target = 0;
        return go(s, h, p.x, p.y, 150);
      }
      return away(s, h, w.cx, w.cy, r.boss.claw.r + 900);
    }
  }
  if (knows && offSwelling(s, h, R)) {
    w.job = -1;
    return;
  }
  // The frost on a knot that should stay shut: strike it off.
  const boss = w.boss ? findEnemy(s, w.boss) : null;
  const want = plan(r, s, knows);
  if (knows && boss && boss.hp > 0 && w.bwarn >= 0 && w.bknot >= 0 && !want.includes(w.bknot)) return strike(s, h, boss);
  if (w.job >= 0) {
    const k = r.knots[w.job] as Pt;
    if (withinRange(h.x, h.y, k.x, k.y, r.reach) && h.moving) orderMove(s, h, h.x, h.y);
    return;
  }
  if (knows && tread(s, h, R, 3200)) return;
  // A cracked bud closes on its own: wait for it, striking what is near.
  if (want.length) {
    // The nearest knot to turn.
    let pick = want[0] as number;
    let pd = 0x7fffffff;
    for (const i of want) {
      const k = r.knots[i] as Pt;
      const d = dist2(h.x, h.y, k.x, k.y);
      if (d < pd) {
        pd = d;
        pick = i;
      }
    }
    return winterOrder(s, h, pick);
  }
  if (guard(s, h, h.x, h.y, 6000)) return;
  if (boss && boss.hp > 0 && knows) return strike(s, h, boss);
};

export const winterBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.winter;
  if (!r) return;
  if (r.boughs) return boughsBot(s, h, knows);
  if (r.hollow) return hollowBot(s, h, knows);
  if (r.sap) return sapBot(s, h, knows);
};
